import ArtworkAtmosphere from "../components/Common/ArtworkAtmosphere";
import styled from "styled-components";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const Page = styled.div`
  background: ${p => p.theme.colors.homeNavy};
  color: ${p => p.theme.colors.white};
  --gold: ${p => p.theme.colors.homeGold};
  --teal: ${p => p.theme.colors.accent};
  a:focus-visible { outline: 3px solid var(--teal); outline-offset: 6px; }
`;
const Intro = styled.section`
  text-align: center;
  padding: 46px 24px 0;
  background: radial-gradient(ellipse at 50% 65%, #103a50 0%, transparent 65%);
  h1 { color: var(--gold); font: 700 clamp(2.5rem, 6vw, 4.5rem)/1.05 "DIN Condensed", "Arial Narrow", sans-serif; letter-spacing: .04em; margin: 12px 0; }
`;
const Eyebrow = styled.p`
  color: var(--teal); font-size: .7rem; font-weight: 700; letter-spacing: .24em; text-transform: uppercase;
`;
const Motto = styled.p`
  color: ${p => p.theme.colors.white}; font-size: clamp(.85rem, 2vw, 1rem); letter-spacing: .12em; margin-bottom: 28px;
`;
// The supplied mockup is 851 × 1847. This window reveals y=85…740 only:
// sky, church and steps, excluding the baked header and large motto.
const Artwork = styled.div`
  position: relative; aspect-ratio: 851 / 655; overflow: hidden;
  width: min(100%, 900px); margin: 0 auto;
  img { position: absolute; width: 100%; max-width: none; top: -12.9771%; left: 0; }
  &::after { content: ""; position: absolute; inset: 0; pointer-events: none;
    box-shadow: inset 0 -25px 28px -18px ${p => p.theme.colors.homeNavy}; }
`;
const Welcome = styled.div`
  padding: 28px 0 48px; max-width: 620px; margin: auto;
  p {
    color: ${p => p.theme.colors.homeGold};
    font-family: ${p => p.theme.fonts.heading};
    font-size: clamp(1rem, 1.7vw, 1.25rem);
    font-weight: 400;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: .06em;
    text-transform: uppercase;
  }
`;
const Button = styled(Link)`
  display: inline-flex; align-items: center; gap: 28px; padding: 14px 24px;
  border: 1px solid var(--teal); background: #104951; color: white;
  font-size: .85rem; font-weight: 600; border-radius: 3px;
  &:hover { background: #17616b; color: white; }
`;
const Section = styled.section`
  max-width: 1200px; margin: auto; padding: 64px 28px;
  border-top: 1px solid #214353;
  h2 { color: var(--gold); font: 500 clamp(2rem, 4vw, 3rem)/1.15 ${p => p.theme.fonts.serif}; }
  @media (max-width: 600px) { padding: 40px 20px; }
`;
const SectionHeading = styled.div`
  display: flex; align-items: end; justify-content: space-between; gap: 24px; margin-bottom: 32px;
  p { color: #bfd2dd; max-width: 370px; margin-bottom: 0; }
  @media (max-width: 700px) { display: block; p { margin-top: 16px; } }
`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 28px;
  @media (max-width: 950px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 600px) { grid-template-columns: 1fr; gap: 28px; }
`;
const Card = styled(Link)`
  display: flex; flex-direction: column; background: #0d293b; color: white;
  border: 1px solid #24495a; border-radius: 4px; overflow: hidden;
  transition: border-color 180ms ease, transform 180ms ease;
  &:hover { color: white; border-color: var(--teal); transform: translateY(-3px); }
  h3 { font: 500 1.6rem/1.2 ${p => p.theme.fonts.serif}; color: var(--gold); margin: 10px 0; }
  p { color: #c6d9e2; font-size: .85rem; line-height: 1.7; }
`;
// A common artwork height keeps the landscape pieces from looking undersized.
// Preserve every subject, inscription and complete architectural frame without cropping.
const MinistryArtwork = styled.div`
  aspect-ratio: 4 / 3; overflow: hidden; background: ${p => p.theme.colors.homeNavy};
  display: flex; align-items: flex-end; justify-content: center;
  img { display: block; width: 100%; height: 100%; object-fit: contain; object-position: center bottom; }
`;
const CardBody = styled.div`
  padding: 22px; display: flex; flex-direction: column; flex: 1;
  .number { font-size: .65rem; letter-spacing: .18em; color: var(--teal); }
  .explore { margin-top: auto; padding-top: 12px; color: var(--teal); font-size: .75rem; letter-spacing: .08em; }
`;
const Mission = styled(Section)`
  display: grid; grid-template-columns: 1fr 1.2fr; gap: 56px;
  p { color: #c6d9e2; line-height: 1.9; }
  a { color: var(--teal); }
  @media (max-width: 700px) { grid-template-columns: 1fr; gap: 12px; }
`;
const ministries = [
  ["The Millennium", "the-millennium", "millennium-framed-v1.png", "A conversation about prophecy and the age to come."],
  ["The Caught-Up Club", "caught-up-club", "caught-up-club-mirrored-recess-final.png", "Music and visual creativity in anticipation of His return."],
  ["Monday Musicians", "monday-musicians", "monday-musicians-beverly-mic-proof-v113.png", "A place for musicians and ministry workers to connect and be heard."],
  ["Care Music Tours", "care-music-tours", "care-paired-singers-v51.png", "Discover the ministry of Care Music Tours."],
  ["Ministry Before Industry (MBI)", "ministry-before-industry", "mbi-framed-v8.png", "Faith and purpose at the heart of our creative work."],
  ["Worship Academy", "worship-academy", "worship-academy-framed-v3.png", "Explore music, worship, and a life of service."],
  ["Quiet Kindness", "quiet-kindness", "quiet-kindness-framed-v6.png", "Mercy and compassion, expressed through everyday kindness."],
];
export default function Home() {
  return <Page>
    <Helmet><title>The Music Church</title><meta name="description" content="Ministry Before Industry" /></Helmet>
    <Intro aria-labelledby="home-title">
      <Eyebrow>Revelation. Purpose. Resonance.</Eyebrow>
      <h1 id="home-title">THE MUSIC CHURCH</h1>
      <Motto>Ministry Before Industry</Motto>
      <ArtworkAtmosphere style={{ maxWidth: "948px", margin: "0 auto" }}><Artwork><img src="/images/home-preview/church-reference.png" alt="The Music Church illuminated beneath a star-filled sky, with teal windows and welcoming stone steps" fetchpriority="high" /></Artwork></ArtworkAtmosphere>
      <Welcome><p>So glad you’re here</p><Button to="/ministries">Explore our ministries <span aria-hidden="true">↗</span></Button></Welcome>
    </Intro>
    <Section aria-labelledby="ministries-title">
      <SectionHeading><div><Eyebrow>Seven ministries. One purpose.</Eyebrow><h2 id="ministries-title">Explore the experience</h2></div><p>Discover music, conversation, and inspiration. Explore at your own pace, and return whenever you like.</p></SectionHeading>
      <Grid>{ministries.map(([title, route, image, description], index) => <Card to={`/ministries/${route}`} key={route}>
        <ArtworkAtmosphere><MinistryArtwork data-artwork={image}><img src={`/images/home-preview/${image}`} alt={`${title} ministry artwork`} loading="lazy" /></MinistryArtwork></ArtworkAtmosphere>
        <CardBody><span className="number">MINISTRY / 0{index + 1}</span><h3>{title}</h3><p>{description}</p><span className="explore">Explore ministry <span aria-hidden="true">↗</span></span></CardBody>
      </Card>)}</Grid>
    </Section>
    <Mission aria-labelledby="mission-title"><div><Eyebrow>At the heart of it all</Eyebrow><h2 id="mission-title">Individual gifts.<br />A shared calling.</h2></div><div><p>We are a support group of imperfect people, united in faith, creativity, and service. A collective of unique diversity, in unity of one purpose: the preparation for the return of The King.</p><p>We follow the path modeled by Jesus through mercy, compassion, and humility, reflecting His love in our daily endeavors.</p><Link to="/about">Get to know The Music Church <span aria-hidden="true">↗</span></Link></div></Mission>
  </Page>;
}
